<template>
  <v-card flat color="#161616" class="rounded-xl pa-8 border-yt">
    <div class="section-title mb-2">
      <v-icon small color="#3ea6ff" class="mr-2">mdi-history</v-icon>
      Historial
    </div>
    <div class="caption grey--text mb-4">
      Quién cambió qué y cuándo: la ficha, las presentaciones, las fotos, los
      proveedores y cada cambio de precio o costo (con su origen: compra, lista,
      importación o a mano).
    </div>

    <v-btn-toggle v-model="filtro" mandatory dense dark class="mb-4">
      <v-btn value="todo" small class="text-none">Todo</v-btn>
      <v-btn value="precios" small class="text-none">Precios y costos</v-btn>
      <v-btn value="producto" small class="text-none">Producto</v-btn>
    </v-btn-toggle>

    <v-progress-linear v-if="loading" indeterminate color="#3ea6ff" />

    <div v-else-if="!eventosFiltrados.length" class="caption grey--text">
      Todavía no hay cambios registrados.
    </div>

    <v-timeline v-else dense align-top class="historial">
      <v-timeline-item
        v-for="(e, i) in eventosFiltrados"
        :key="i"
        small
        :color="color(e)"
        fill-dot
      >
        <div class="d-flex flex-wrap align-center" style="gap: 6px">
          <span class="white--text font-weight-medium">{{ titulo(e) }}</span>
          <v-chip v-if="e.origen && e.origen !== 'MANUAL'" x-small outlined color="#3ea6ff">{{ origen(e.origen) }}</v-chip>
          <span v-if="e.presentacion || e.sucursal" class="caption grey--text">
            {{ [e.presentacion, e.sucursal].filter(Boolean).join(' · ') }}
          </span>
        </div>
        <div class="caption grey--text mb-1">
          {{ fecha(e.fecha) }}<template v-if="e.usuario"> · {{ e.usuario }}</template>
        </div>
        <div v-for="(valores, campo) in e.cambios" :key="campo" class="caption cambio">
          <span class="grey--text">{{ etiqueta(campo) }}:</span>
          <template v-if="e.accion === 'MODIFICACION'">
            <span class="antes">{{ mostrar(campo, valores[0]) }}</span>
            <v-icon x-small color="grey">mdi-arrow-right</v-icon>
          </template>
          <span class="white--text">{{ mostrar(campo, e.accion === 'BAJA' ? valores[0] : valores[1]) }}</span>
        </div>
      </v-timeline-item>
    </v-timeline>
  </v-card>
</template>

<script>
const ENTIDADES = {
  PRODUCTO: "Producto",
  PRESENTACION: "Presentación",
  SUCURSAL: "Sucursal",
  VARIANTE: "Variante",
  IMAGEN: "Foto",
  PROVEEDOR: "Proveedor",
  PRECIO: "Precio",
};
const ACCIONES = { ALTA: "alta", MODIFICACION: "cambio", BAJA: "baja" };
const ORIGENES = {
  COMPRA: "Compra",
  PROMEDIO: "Costo promedio",
  COMBO: "Costo del combo",
  PRODUCCION: "Producción",
  IMPORTACION: "Importación",
  ALTA_MASIVA: "Alta masiva",
  LISTA_PROVEEDOR: "Lista del proveedor",
};
const ETIQUETAS = {
  descripcion_comercial: "Descripción",
  categoria_id: "Categoría",
  marca_id: "Marca",
  proveedor_id: "Proveedor",
  tipo_stock: "Tipo de stock",
  metodo_costo: "Método de costo",
  porcentaje_merma: "Merma %",
  codigo_interno_empresa: "Código interno",
  margen_ganancia_sugerido: "Margen sugerido %",
  activo_en_empresa: "Activo",
  factor_base: "Factor",
  es_base: "Es base",
  es_vendible: "Se vende",
  es_comprable: "Se compra",
  codigo_externo: "Código del proveedor",
  codigo_barras: "Código de barras",
  costo: "Costo",
  precio_venta: "Precio de venta",
  ganancia: "Ganancia %",
  activo: "Activa",
  archivo: "Archivo",
  principal: "Principal",
  composicion: "Composición",
};
const PESOS = ["costo", "precio_venta"];

export default {
  name: "ProductoEmpresaHistorial",
  data() {
    return { eventos: [], loading: false, filtro: "todo" };
  },
  computed: {
    eventosFiltrados() {
      if (this.filtro === "precios") return this.eventos.filter((e) => e.entidad === "PRECIO");
      if (this.filtro === "producto") return this.eventos.filter((e) => e.entidad !== "PRECIO");
      return this.eventos;
    },
  },
  methods: {
    titulo(e) {
      const entidad = ENTIDADES[e.entidad] || e.entidad;
      return `${entidad}: ${ACCIONES[e.accion] || e.accion}`;
    },
    origen(codigo) {
      return ORIGENES[codigo] || codigo;
    },
    color(e) {
      if (e.accion === "BAJA") return "red darken-2";
      if (e.accion === "ALTA") return "green darken-2";
      return e.entidad === "PRECIO" ? "amber darken-3" : "blue darken-2";
    },
    etiqueta(campo) {
      return ETIQUETAS[campo] || campo;
    },
    mostrar(campo, valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      if (typeof valor === "boolean") return valor ? "Sí" : "No";
      if (Array.isArray(valor)) return valor.join(", ");
      if (PESOS.includes(campo)) {
        return Number(valor).toLocaleString("es-AR", { style: "currency", currency: "ARS" });
      }
      return valor;
    },
    fecha(valor) {
      return valor
        ? new Date(valor).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })
        : "—";
    },
    async fetch() {
      this.loading = true;
      try {
        const { data } = await this.axios.get(
          `/productos/productos/${this.$route.params.id_producto}/historial`
        );
        this.eventos = data;
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetch();
  },
};
</script>

<style scoped>
.cambio {
  line-height: 1.6;
}
.antes {
  color: #9e9e9e;
  text-decoration: line-through;
  margin: 0 4px;
}
</style>
